<template>
  <div class="container page order-number-tracking-page">
    <PageHeader
      :title="'Track your order'"
      :breadcrumbItems="breadcrumbItems"
    />
    <div class="order-tracking-form" v-if="!orderTracking">
      <form @submit.prevent="fetchData">
        <sib-form-item
          label="Email"
          input-id="order-tracking-form__email"
          :value="requestTracking.email"
          :is-required="true"
          :validator="isEmail"
          ref="itemEmail"
          error-message="Email is invalid"
        >
          <sib-input
            id="order-tracking-form__email"
            size="sm"
            v-model="requestTracking.email"
            name="email"
          ></sib-input>
        </sib-form-item>

        <sib-form-item
          label="Order number"
          input-id="order-tracking-form__order-number"
          :value="requestTracking.orderNumber"
          :is-required="true"
          ref="itemOrderNumber"
        >
          <sib-input
            id="order-tracking-form__order-number"
            size="sm"
            v-model="requestTracking.orderNumber"
            name="order-number"
          ></sib-input>
        </sib-form-item>

        <div v-if="trackingStore.error" class="order-tracking-form__error-note">
          Something went wrong. Please try again later.
        </div>

        <sib-button
          class="order-tracking-form__submit-button sib-button--width-full"
          role="primary"
          :loading="loading"
        >
          Check
        </sib-button>
      </form>
    </div>

    <div v-else class="order-tracking-content">
      <h2 class="order-number">Order number: {{ orderTracking.name }}</h2>
      <div v-show="orderShipToAddress">
        <label><b>Ship to:</b></label>
        {{ orderShipToAddress }}
      </div>
      <div
        class="tracking"
        v-for="(tracking, i) in orderTracking.trackings"
        :key="i"
      >
        <div class="tracking__part">
          <h2 class="m-0">
            Tracking number:
            <a
              v-if="tracking.tracking_url"
              :href="tracking.tracking_url"
              target="_blank"
              >{{ tracking.tracking_number }}</a
            >
          </h2>
          <div>
            Status:
            <b class="tracking__status">{{ tracking.tracking_status }}</b>
          </div>
        </div>

        <TrackingTimelines :tracking="tracking"></TrackingTimelines>

        <TrackingCollapsible>
          <h3 class="m-0">Tracking details</h3>
          <TrackingDetails
            :historyString="tracking.history_checkpoint_description"
          />
        </TrackingCollapsible>

        <TrackingCollapsible>
          <h3 class="m-0">Items</h3>
          <template v-for="(item, i) in tracking.items" :key="i">
            <TrackingItem :trackingItem="item" />
          </template>
        </TrackingCollapsible>
      </div>

      <sib-button
        @click="checkAnotherOrder"
        class="sib-button--width-full mt-md"
      >
        Check another order
      </sib-button>

      <client-only>
        <TrackingProductsRecommendation
          :products="trackingStore.productsRecommendationTracking || []"
        />
      </client-only>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed, onServerPrefetch, ref, onMounted, watch } from 'vue'
import PageHeader from '@/components/PageHeader.vue'
import TrackingDetails from './TrackingDetails.vue'
import TrackingCollapsible from './TrackingCollapsible.vue'
import TrackingItem from './TrackingItem.vue'
import TrackingTimelines from './TrackingTimelines.vue'
import TrackingProductsRecommendation from './TrackingProductsRecommendation.vue'
import { useFetchData } from '@/composables/fetchData'
import useTrackingStore from '@/store/tracking'
import useUpdateRouter from '@/composables/router'
import { useRoute } from 'vue-router'
import FormItem from '@/ui-kits/FormItem.vue'
import { isEmail } from '@/utils'
import { ShippingAdress } from '@/types/tracking'
import { isOnServer } from '@/utils/ssr'
import SibFormItem from '@/ui-kits/FormItem.vue'
import LocationService, { UserLocation } from '@/services/location'

const route = useRoute()
const { updateQueryString } = useUpdateRouter()
const trackingStore = useTrackingStore()
const { loading, fetchData } = useFetchData({
  fetchDataFunction: fetchStatusOrderNumber,
})

const itemOrderNumber = ref<typeof FormItem | null>(null)
const itemEmail = ref<typeof FormItem | null>(null)

const requestTracking = computed(() => trackingStore.requestTracking)
const orderTracking = computed(() => trackingStore.orderTracking)
const orderShipToAddress = computed(() =>
  shipToAddress(trackingStore.orderTracking?.shipping_address)
)
const breadcrumbItems = [
  {
    url: `/tracking`,
    name: 'Track your order',
  },
]

onServerPrefetch(async () => {
  getTrackingDataFromRoute()
  await fetchData()
})

onMounted(() => {
  watch(
    () => orderTracking.value,
    async (value) => {
      if (!value) return
      getProductsRecommendationTrackingPage()
    },
    {
      immediate: true,
    }
  )
})

async function fetchStatusOrderNumber() {
  trackingStore.error = null

  // do not get data without email or password in ssr
  if (
    (!requestTracking.value.email || !requestTracking.value.orderNumber) &&
    isOnServer
  )
    return

  //don't call in ssr mode because ref can only call after mounted
  if (!isOnServer) {
    const isEmailValid = itemEmail.value?.validate()
    const isOrderNumberValid = itemOrderNumber.value?.validate()

    const isValid = isEmailValid && isOrderNumberValid
    if (!isValid) return
  }

  // set the query string for route
  updateQueryString({
    email: requestTracking.value.email,
    order_number: requestTracking.value.orderNumber,
  })

  return await trackingStore.getStatusOrderNumber(requestTracking.value)
}
function getTrackingDataFromRoute() {
  for (const key in route.query) {
    if (Object.prototype.hasOwnProperty.call(route.query, key)) {
      if (key === 'email')
        trackingStore.requestTracking.email = route.query[key] as string
      if (key === 'order_number')
        trackingStore.requestTracking.orderNumber = route.query[key] as string
    }
  }
}

function getFullString(strs: string[]) {
  return strs.filter((str: string) => !!str).join(', ')
}

function shipToAddress(shippingAddress?: ShippingAdress) {
  if (!shippingAddress) return ''
  return getFullString([
    shippingAddress.address2,
    shippingAddress.address1,
    shippingAddress.city,
    shippingAddress.province,
    shippingAddress.country,
  ])
}
function checkAnotherOrder() {
  trackingStore.orderTracking = null
  trackingStore.productsRecommendationTracking = []
  window.scrollTo({ top: 0, behavior: 'smooth' })
}

async function getProductsRecommendationTrackingPage() {
  LocationService.onLocationReady(async (location: UserLocation | null) => {
    if (
      !location?.ip ||
      !trackingStore.requestTracking.email ||
      !trackingStore.requestTracking.orderNumber
    )
      return
    const payload = {
      ip: location.ip,
      email: trackingStore.requestTracking.email,
      order: trackingStore.requestTracking.orderNumber,
    }

    await trackingStore.getProductsRecommendationTrackingPage(payload)
  })
}
</script>

<style lang="scss">
.order-number-tracking-page {
  --order-tracking-background: #efefef;
  .order-tracking-form {
    max-width: 500px;
    margin: 3rem auto;
    &__submit-button {
      text-transform: uppercase;
      margin-top: 0.6em;
    }
    &__error-note {
      font-size: 0.875em;
      color: var(--color-secondary);
    }
  }
  .order-number {
    text-transform: uppercase;
    margin: 2em 0 0.5em;
    font-weight: bold;
  }
  .tracking {
    border: solid 1px #ccc;
    margin-top: 2em;
    background-color: var(--order-tracking-background);
    box-shadow: 0 -10px 10px -10px var(--text-color-secondary);
    border-radius: 3px;
    &__status {
      color: var(--color-primary);
    }
    &__part {
      padding: 1.25em 1em;
      border-bottom: solid 1px #fff;
    }
  }
  h2:not(.products-recommendation-title) {
    font-size: 1.15rem;
    font-weight: 600;
  }
  h3 {
    font-size: 1rem;
    margin-bottom: 0.75em;
  }
}
</style>
